import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { MonthlyTrendChart } from "../../components/charts/MonthlyTrendChart";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { formatUsdPerLb } from "./callForm";
import { errorMessage, monthName, shortDateLabel } from "./format";
import { MonthRangeSlider } from "./MonthRangeSlider";
import { PricePointFilterBar } from "./PricePointFilterBar";
import {
  ALL_PRICE_POINTS,
  callChartX,
  callVsLoggedPricesReadout,
  clampRange,
  filterPricePoints,
  priceTypeOptions,
  resinCodeOptions,
  type PricePointFilters,
  historyMonths,
  monthlyCallsInRange,
  monthsBetween,
  plottedPricePoints,
  pricePointChartX,
  visibleForecast,
  type MonthRange,
} from "./priceContextChart";
import { SectionHeader } from "./SectionHeader";
import { pricePointDrawerContent, type SourceDrawerContent } from "./sourceDrawerContent";
import type { PriceHistory } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  logIntelligenceVersion: number;
  callVersion: number;
  onOpenDrawer: (content: SourceDrawerContent) => void;
}

interface LoadedPriceHistory {
  loadKey: string;
  history: PriceHistory;
}

interface ChosenRange extends MonthRange {
  loadKey: string;
}

interface ChosenFilters {
  loadKey: string;
  filters: PricePointFilters;
}

const EMPTY_TEXT_CLASSES = "m-0 text-rs-light-gray text-[13px]";

export function PricesInContext({
  month,
  resinFamilyCode,
  logIntelligenceVersion,
  callVersion,
  onOpenDrawer,
}: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const loadKey = `${resinFamilyCode}|${month}`;
  const [loadedHistory, setLoadedHistory] = useState<LoadedPriceHistory | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chosenRange, setChosenRange] = useState<ChosenRange | null>(null);
  const [chosenFilters, setChosenFilters] = useState<ChosenFilters | null>(null);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(
        `consultant-cockpit/price-history?resinFamily=${encodeURIComponent(resinFamilyCode)}&toMonth=${month}-01`,
      )
      .then((response) => response.json())
      .then((history: PriceHistory) => {
        if (isStale) return;
        setLoadedHistory({ loadKey, history });
        setLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, loadKey, logIntelligenceVersion, callVersion]);

  const currentHistory = loadedHistory?.loadKey === loadKey ? loadedHistory.history : null;
  const months = useMemo(() => historyMonths(currentHistory, month), [currentHistory, month]);
  const { fromMonth, toMonth } = clampRange(months, chosenRange?.loadKey === loadKey ? chosenRange : null);
  const visibleMonths = useMemo(() => monthsBetween(fromMonth, toMonth), [fromMonth, toMonth]);

  const chart = useMemo(() => {
    if (!currentHistory) return null;
    const visibleRange = { fromMonth, toMonth };
    const monthlyCalls = monthlyCallsInRange(currentHistory.calls, visibleRange);
    return {
      monthlyCalls,
      pricePoints: plottedPricePoints(currentHistory.pricePoints, visibleRange),
      forecast: visibleForecast(currentHistory.forecast, monthlyCalls, visibleRange, month),
    };
  }, [currentHistory, fromMonth, toMonth, month]);

  const forecastMonths = chart?.forecast?.months.map((forecastMonth) => forecastMonth.month) ?? [];
  const plottedMonths = [...visibleMonths, ...forecastMonths];
  const forecastTitle = forecastMonths.length
    ? `, forecast to ${monthName(forecastMonths[forecastMonths.length - 1])}`
    : "";
  const rangeTitle = `${resinFamilyCode} · $/lb · ${monthName(fromMonth)} to ${monthName(toMonth)}${forecastTitle}`;

  const pricePointsInRange = chart?.pricePoints ?? [];
  const priceTypes = priceTypeOptions(pricePointsInRange);
  const resinCodes = resinCodeOptions(pricePointsInRange);
  const requestedFilters = chosenFilters?.loadKey === loadKey ? chosenFilters.filters : ALL_PRICE_POINTS;
  const filters: PricePointFilters = {
    priceTypeCode:
      requestedFilters.priceTypeCode !== null && priceTypes.includes(requestedFilters.priceTypeCode)
        ? requestedFilters.priceTypeCode
        : null,
    resinCode:
      requestedFilters.resinCode !== null && resinCodes.includes(requestedFilters.resinCode)
        ? requestedFilters.resinCode
        : null,
  };
  const filteredPricePoints = filterPricePoints(pricePointsInRange, filters);
  const readout = chart
    ? callVsLoggedPricesReadout(resinFamilyCode, toMonth, chart.monthlyCalls, filteredPricePoints)
    : null;

  return (
    <section className="lane on" aria-labelledby="prices-heading">
      <SectionHeader sectionId="prices-section">
        <Chip>{monthName(month)}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      <Card>
        <h3 className="m-0 mb-3 text-[15px]">{rangeTitle}</h3>
        {loadError ? (
          <p className={EMPTY_TEXT_CLASSES}>Couldn't load prices: {loadError}</p>
        ) : !chart ? (
          <p className={EMPTY_TEXT_CLASSES}>Loading…</p>
        ) : (
          <>
            <MonthRangeSlider
              months={months}
              fromMonth={fromMonth}
              toMonth={toMonth}
              onChange={(chosenFromMonth, chosenToMonth) =>
                setChosenRange({ loadKey, fromMonth: chosenFromMonth, toMonth: chosenToMonth })
              }
            />
            <PricePointFilterBar
              priceTypes={priceTypes}
              resinCodes={resinCodes}
              filters={filters}
              onChange={(nextFilters) => setChosenFilters({ loadKey, filters: nextFilters })}
            />
            {chart.monthlyCalls.length === 0 && chart.pricePoints.length === 0 ? (
              <p className={EMPTY_TEXT_CLASSES}>
                No calls or price points logged for {monthName(fromMonth)} to {monthName(toMonth)}.
              </p>
            ) : (
              <>
                <MonthlyTrendChart
                  months={plottedMonths}
                  highlightedMonth={month}
                  line={{
                    label: "My FMP calls",
                    points: chart.monthlyCalls.map((monthlyCall) => ({
                      monthIndex: callChartX(plottedMonths, monthlyCall),
                      value: monthlyCall.fmpUsdPerLb,
                      tooltip: `Your ${monthName(monthlyCall.month)} call: ${formatUsdPerLb(monthlyCall.fmpUsdPerLb)}`,
                    })),
                  }}
                  markers={{
                    label: "Logged price points",
                    points: filteredPricePoints.map((plottedPoint) => ({
                      x: pricePointChartX(plottedMonths, plottedPoint),
                      value: plottedPoint.priceUsdPerLb,
                      tooltip: `${formatUsdPerLb(plottedPoint.priceUsdPerLb)} · ${plottedPoint.pricePoint.priceTypeCode} · ${plottedPoint.pricePoint.location} · ${shortDateLabel(plottedPoint.pricePoint.heardDate)}`,
                      paletteIndex: priceTypes.indexOf(plottedPoint.pricePoint.priceTypeCode),
                    })),
                    onMarkerClick: (markerIndex) =>
                      onOpenDrawer(pricePointDrawerContent(filteredPricePoints[markerIndex].pricePoint)),
                  }}
                  forecast={
                    chart.forecast && {
                      label: "My forecast",
                      startMonthIndex: visibleMonths.length,
                      anchor: chart.forecast.anchor && {
                        monthIndex: callChartX(plottedMonths, chart.forecast.anchor),
                        value: chart.forecast.anchor.fmpUsdPerLb,
                      },
                      points: chart.forecast.months.map((forecastMonth) => ({
                        monthIndex: plottedMonths.indexOf(forecastMonth.month),
                        value: forecastMonth.fmpUsdPerLb,
                        tooltip: `Forecast for ${monthName(forecastMonth.month)} (from your ${monthName(chart.forecast?.fromCallMonth ?? month)} call): ${formatUsdPerLb(forecastMonth.fmpUsdPerLb)}`,
                      })),
                    }
                  }
                  formatValue={(valueUsdPerLb) => `$${valueUsdPerLb.toFixed(2)}`}
                  ariaLabel={`${rangeTitle}: your monthly calls, forecast and logged price points`}
                />
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-[13px] text-rs-light-gray">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-4 h-0.5 bg-rs-green" aria-hidden="true" />
                    My FMP calls
                  </span>
                  {chart.forecast && (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 border-t-2 border-dashed border-rs-green" aria-hidden="true" />
                      My forecast · from the {monthName(chart.forecast.fromCallMonth)} call
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-rs-off-white" aria-hidden="true" />
                    {priceTypes.length > 1
                      ? "Logged price points · colored by type · click to open"
                      : "Logged price points · click to open"}
                  </span>
                  {chart.monthlyCalls.length === 0 && <span>You haven't saved a call in these months yet.</span>}
                  {chart.pricePoints.length === 0 && <span>No price points logged in these months yet.</span>}
                </div>
                {readout && <p className="m-0 mt-2 text-[13px] text-rs-off-white">{readout}</p>}
              </>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
